import { useEffect, useRef, useState, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Anchor, CheckCircle2, ChevronRight,
  Layers, Move, Navigation, Package, Pencil, Truck, Wrench,
} from 'lucide-react';
import { AnimateIn, Reveal } from '../../shared/ui';
import { DirectContact } from './DirectContact';
import { useApprovedReviews } from '../../features/reviews/hooks';
import { usePageMeta } from '../../shared/hooks/usePageMeta';
import type { Review } from '../../shared/types/app';

const PARISHES = ['Kingston', 'St. Andrew', 'St. Catherine', 'Clarendon', 'Manchester', 'St. Elizabeth', 'Westmoreland', 'Hanover', 'St. James', 'Trelawny', 'St. Ann', 'St. Mary', 'Portland', 'St. Thomas'];

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const pillPrimary =
  'inline-flex items-center gap-2 rounded-full bg-accent font-medium text-black transition-all duration-300 hover:scale-[1.04] hover:bg-[#ffb84a] hover:shadow-[0_10px_30px_rgba(245,165,36,0.35)] active:scale-95';

// Looping decorations (road, truck, parish rows) speed up with scroll velocity and ease back to
// normal when scrolling stops. Elements opt in with data-scroll-speed.
function useScrollSpeed() {
  useEffect(() => {
    if (reducedMotion()) return;
    let lastY = window.scrollY;
    let boost = 0;
    let raf = 0;
    const tick = () => {
      const y = window.scrollY;
      const v = Math.abs(y - lastY);
      lastY = y;
      // Fast attack, slow decay so the speed-up eases out instead of snapping back.
      boost = Math.max(Math.min(v * 0.12, 8), boost * 0.94);
      const rate = 1 + boost;
      document.querySelectorAll('[data-scroll-speed]').forEach((el) => {
        el.getAnimations().forEach((a) => { a.playbackRate = rate; });
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
}

function Hero() {
  const contentRef = useRef<HTMLDivElement>(null);

  // Hero copy drifts down and fades as the page scrolls.
  useEffect(() => {
    if (reducedMotion() || window.innerWidth < 1024) return;
    const el = contentRef.current;
    if (!el) return;
    const onScroll = () => {
      const y = window.scrollY;
      el.style.transform = `translateY(${y * 0.25}px)`;
      el.style.opacity = String(Math.max(0, 1 - y / 700));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="relative flex flex-col items-center px-[22px] pt-[clamp(56px,11vw,120px)] text-center">
      <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[-120px] overflow-hidden" aria-hidden>
        <div className="mk-orbit absolute left-1/2 top-10 -ml-[560px] h-[720px] w-[720px] rounded-full" style={{ ['--dx' as string]: '140px', ['--dy' as string]: '60px', background: 'radial-gradient(circle,rgba(245,165,36,0.22),rgba(245,165,36,0) 65%)' }} />
        <div className="mk-orbit absolute left-1/2 top-40 -ml-10 h-[560px] w-[560px] rounded-full" style={{ ['--dx' as string]: '-160px', ['--dy' as string]: '80px', background: 'radial-gradient(circle,rgba(255,120,40,0.16),rgba(255,120,40,0) 65%)' }} />
      </div>

      <div ref={contentRef} className="relative flex flex-col items-center">
        <Reveal immediate delay={100} className="mb-3 text-[21px] font-semibold tracking-[-0.015em] text-accent">
          Jamaica's Equipment Haulage Platform
        </Reveal>
        <Reveal as="h1" kind="blur" immediate delay={250} className="m-0 max-w-[980px] text-[clamp(42px,10vw,96px)] font-bold leading-[1.02] tracking-[-0.045em] [text-wrap:balance]">
          Heavy equipment.<br />Moved in one tap.
        </Reveal>
        <Reveal as="p" immediate delay={600} className="mt-5 max-w-[620px] text-[clamp(19px,2.2vw,24px)] leading-[1.35] tracking-[-0.015em] text-[#a1a1a6] [text-wrap:balance]">
          Book a lowboy or flatbed anywhere in Jamaica. Our own trained drivers, live tracking to site, and a team that confirms your price directly.
        </Reveal>
        <Reveal immediate delay={800} className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link to="/signup" className={`${pillPrimary} px-6 py-3 text-[17px]`}>
            Request a haul <ArrowRight size={18} strokeWidth={2.5} />
          </Link>
          <a href="#how" className="inline-flex items-center gap-0.5 px-2 py-3 text-[17px] text-accent hover:underline">
            See how it works <ChevronRight size={16} strokeWidth={2.5} />
          </a>
        </Reveal>
      </div>

      <Reveal immediate delay={1100} className="relative mt-[clamp(48px,8vw,96px)] h-[72px] w-full max-w-[1200px] overflow-hidden">
        <div data-scroll-speed className="mk-road absolute inset-x-0 bottom-3 h-[3px]" style={{ backgroundImage: 'repeating-linear-gradient(90deg,#3a3a3c 0 40px,transparent 40px 80px)', backgroundSize: '80px 3px' }} aria-hidden />
        <div data-scroll-speed className="mk-drive absolute bottom-[18px] text-accent" aria-hidden>
          <span className="mk-bob block"><Truck size={40} strokeWidth={2} /></span>
        </div>
      </Reveal>
    </section>
  );
}

const SERVICES = [
  { kicker: 'Lowboy', title: 'Excavators & dozers', body: 'Tracked machines up to 40 tonnes, loaded and strapped by experienced crews.', Icon: Truck },
  { kicker: 'Flatbed', title: 'Backhoes & loaders', body: 'Wheeled plant moved between sites the same day where routes allow.', Icon: Wrench },
  { kicker: 'Light haul', title: 'Rollers & compactors', body: 'Smaller machines and attachments, grouped into one trip when it saves you money.', Icon: Layers },
  { kicker: 'Oversized', title: 'Containers & special loads', body: 'Containers, generators and wide loads, with permits and escorts arranged.', Icon: Package },
  { kicker: 'Rollback', title: 'Forklifts & skid steers', body: 'Compact machines and vehicles winched onto a tilt bed, no ramps needed.', Icon: Move },
  { kicker: 'Step deck', title: 'Steel, pipe & materials', body: 'Long and bulky loads taken from the port or supplier straight to your site.', Icon: Anchor },
];

function TiltCard({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const onMove = (e: ReactMouseEvent) => {
    const el = ref.current;
    if (!el || reducedMotion()) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${-py * 8}deg) rotateY(${px * 10}deg) translateY(-6px)`;
  };
  const onLeave = () => { if (ref.current) ref.current.style.transform = ''; };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="flex flex-col rounded-[28px] bg-[#161617] px-[clamp(24px,5vw,36px)] py-[clamp(28px,6vw,40px)] transition-[transform,background-color] duration-500 will-change-transform hover:bg-[#1c1c1e]"
    >
      {children}
    </div>
  );
}

function Services() {
  return (
    <section id="services" className="mx-auto max-w-[1200px] scroll-mt-16 px-[22px] pt-[clamp(80px,12vw,140px)]">
      <Reveal as="h2" className="m-0 mb-12 max-w-[760px] text-[clamp(40px,5.6vw,64px)] font-bold leading-[1.05] tracking-[-0.04em] [text-wrap:balance]">
        If it's heavy, <span className="text-[#6e6e73]">we move it.</span>
      </Reveal>
      <Reveal kind="stagger" className="grid gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),1fr))]">
        {SERVICES.map(({ kicker, title, body, Icon }) => (
          <TiltCard key={title}>
            <Reveal kind="draw" as="span" className="mb-6 grid h-14 w-14 place-items-center rounded-full bg-[#2a1d06] text-accent">
              <Icon size={26} strokeWidth={2} />
            </Reveal>
            <div className="mb-1.5 text-sm font-semibold text-accent">{kicker}</div>
            <div className="text-[clamp(26px,6vw,32px)] font-bold leading-[1.1] tracking-[-0.03em]">{title}</div>
            <div className="mt-2.5 max-w-[380px] text-[17px] text-[#a1a1a6]">{body}</div>
          </TiltCard>
        ))}
      </Reveal>
    </section>
  );
}

const STEPS = [
  { title: 'Tell us what and where.', body: "Pick the machine, pickup and drop-off. Add photos or dimensions if it's unusual.", Icon: Pencil },
  { title: 'Get a price. Confirm.', body: 'Our team confirms the full cost with you before you book. We assign one of our drivers and the right trailer.', Icon: CheckCircle2 },
  { title: 'Track it to site.', body: 'Follow the truck live and get notified at pickup and delivery.', Icon: Navigation },
];

function HowItWorks() {
  return (
    <section id="how" className="mx-auto max-w-[1200px] scroll-mt-16 px-[22px] pt-[clamp(96px,14vw,160px)]">
      <Reveal as="h2" className="m-0 mb-[clamp(36px,6vw,64px)] text-center text-[clamp(40px,5.6vw,64px)] font-bold leading-[1.05] tracking-[-0.04em]">
        Three steps. <span className="text-[#6e6e73]">No phone tag.</span>
      </Reveal>
      <Reveal kind="stagger" className="grid gap-[clamp(32px,5vw,48px)] [grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr))]">
        {STEPS.map(({ title, body, Icon }, i) => (
          <div key={title} className="flex flex-col gap-3 border-t-2 border-[#333336] pt-7">
            <div className="flex items-center justify-between">
              <div className="text-[56px] font-bold leading-none tracking-[-0.04em] text-accent">{i + 1}</div>
              <Reveal kind="draw" as="span" className="text-[#86868b]"><Icon size={28} strokeWidth={2} /></Reveal>
            </div>
            <div className="text-2xl font-semibold tracking-[-0.02em]">{title}</div>
            <div className="max-w-[320px] text-[#a1a1a6]">{body}</div>
          </div>
        ))}
      </Reveal>
    </section>
  );
}

function Counter({ end }: { end: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [n, setN] = useState(end);

  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion()) return;
    setN(0);
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const step = (t: number) => {
        const p = Math.min(1, (t - start) / 1800);
        setN(Math.round(end * (1 - Math.pow(1 - p, 4))));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [end]);

  return <div ref={ref} className="text-[clamp(80px,14vw,180px)] font-bold leading-[0.9] tracking-[-0.06em]">{n}</div>;
}

function Coverage() {
  const row = (reverse: boolean) => (
    <div data-scroll-speed className={`mk-marquee flex w-max gap-3 ${reverse ? 'mk-marquee-rev' : ''}`} aria-hidden>
      {[...PARISHES, ...PARISHES].map((p, i) => (
        <span key={i} className="flex-none whitespace-nowrap rounded-full border-2 border-[#2c2c2e] px-[22px] py-3 text-[17px] font-medium text-[#a1a1a6]">{p}</span>
      ))}
    </div>
  );
  const mask = 'linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)';
  return (
    <section id="coverage" className="scroll-mt-16 px-[22px] pt-[clamp(96px,14vw,160px)] text-center">
      <Counter end={PARISHES.length} />
      <Reveal delay={200} className="mt-3 text-[clamp(24px,3vw,32px)] font-semibold tracking-[-0.025em]">
        parishes. Kingston to Negril.
      </Reveal>
      <p className="sr-only">We serve {PARISHES.join(', ')}.</p>
      <div className="mt-14 flex flex-col gap-3 overflow-hidden" style={{ WebkitMaskImage: mask, maskImage: mask }}>
        {row(false)}
        {row(true)}
      </div>
    </section>
  );
}

function reviewerInitials(name: string): string {
  const [first = '', last = ''] = name.trim().split(/\s+/);
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?';
}

function ReviewCard({ review, featured }: { review: Review; featured: boolean }) {
  return (
    <div className={featured ? 'rounded-[32px] bg-[#161617] p-10 md:p-16' : 'flex h-full flex-col rounded-[28px] bg-[#161617] p-8'}>
      <p className={featured ? 'mb-6 text-7xl font-black leading-none text-white/20' : 'mb-4 text-5xl font-black leading-none text-white/20'}>
        "
      </p>
      <blockquote
        className={
          featured
            ? 'mb-8 max-w-3xl text-2xl font-bold leading-snug text-white md:text-3xl'
            : 'mb-6 flex-1 text-lg font-bold leading-snug text-white'
        }
      >
        {review.comment}
      </blockquote>
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-accent text-sm font-black text-black">
          {reviewerInitials(review.reviewerName)}
        </div>
        <div>
          <p className="text-sm font-bold text-white">{review.reviewerName}</p>
          {review.reviewerCompany && <p className="text-sm text-white/40">{review.reviewerCompany}</p>}
        </div>
      </div>
    </div>
  );
}

function Testimonial() {
  const { data: reviews, isLoading } = useApprovedReviews(3);

  if (isLoading) {
    return (
      <section className="pt-[clamp(96px,14vw,160px)]">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-6 px-[22px] sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="mb-2 h-4 w-full rounded bg-white/10" />
              <div className="mb-6 h-4 w-3/4 rounded bg-white/10" />
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-white/10" />
                <div className="h-3 w-24 rounded bg-white/10" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // No error/retry UI here on purpose — reviews are decorative content on a public marketing
  // page, not something worth surfacing an error state for; a failed or empty fetch just
  // degrades to hiding the whole section.
  if (!reviews || reviews.length === 0) return null;

  return (
    <section className="pt-[clamp(96px,14vw,160px)]">
      <div className="mx-auto max-w-[1200px] px-[22px]">
        {reviews.length === 1 ? (
          <AnimateIn animation="scale-in">
            <ReviewCard review={reviews[0]} featured />
          </AnimateIn>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {reviews.map((review, i) => (
              <AnimateIn key={review.id} animation="scale-in" delay={i * 100}>
                <ReviewCard review={review} featured={false} />
              </AnimateIn>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="flex flex-col items-center gap-7 px-[22px] pb-[clamp(80px,12vw,140px)] pt-[clamp(96px,14vw,160px)] text-center">
      <Reveal as="h2" kind="blur" className="m-0 max-w-[860px] text-[clamp(44px,7vw,80px)] font-bold leading-[1.02] tracking-[-0.045em] [text-wrap:balance]">
        Got a machine that needs moving?
      </Reveal>
      <Link to="/signup" className={`mk-pulse ${pillPrimary} px-7 py-3.5 text-[19px]`}>
        Request a haul <ArrowRight size={18} strokeWidth={2.5} />
      </Link>
    </section>
  );
}

export function HomePage() {
  useScrollSpeed();
  usePageMeta('Home', 'Move heavy equipment across Jamaica with one tap — book haulage, track your job, and pay online with RT Equipment & Haulage.');
  return (
    <div className="overflow-x-clip bg-black text-[#f5f5f7]">
      <Hero />
      <Services />
      <HowItWorks />
      <Coverage />
      <Testimonial />
      <DirectContact />
      <CTA />
    </div>
  );
}
